import { useEffect, useState } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { LTR, SUPPORTED_LANGUAGES } from 'arbitrip-common/translation/const';
import { RM3_DETAILS_SOURCE } from 'arbitrip-common/client/utils/HotelDealsRm3';

import Ajax from '@/utils/Ajax';

const SETTING_PATH = 'company.settings.enable_hotel_info_translation';
const ENGLISH_HEADINGS = Object.freeze({ lng: SUPPORTED_LANGUAGES.EN });

/** The text sent for translation (spec P2.9): the catalog description, else the group paragraph, else its title. */
export function translatableDescriptionOf(details) {
    const room = details.room || {};
    if (details.source === RM3_DETAILS_SOURCE.CATALOG) {
        return room.description || '';
    }
    return room.roomDescriptionParagraph || room.roomDescriptionTitle || '';
}

/**
 * Today's room-dialog translation (`RoomsCatalogDialog`), unchanged: one request per mount (each modal open is a
 * fresh mount), the original on failure or with "show original", and English headings over the English original.
 */
function useRm3RoomTranslation(details, profile) {
    const { i18n } = useTranslation();
    const enabled = !!_.get(profile, SETTING_PATH, false);
    const amenities = details.amenities;
    const description = translatableDescriptionOf(details);
    const hasContent = amenities.length > 0 || !!description.trim();
    const translatedExists = enabled && !!i18n.language && i18n.language !== SUPPORTED_LANGUAGES.EN && hasContent;

    const [translated, setTranslated] = useState({ amenities: null, description: null });
    const [translating, setTranslating] = useState(false);
    const [showOriginal, setShowOriginal] = useState(false);

    useEffect(() => {
        if (!translatedExists) {
            return undefined;
        }
        let cancelled = false;
        setTranslating(true);
        Ajax.translateRoom({ amenities: [...amenities], description })
            .done((res) => {
                if (cancelled) {
                    return;
                }
                setTranslated({
                    amenities: Array.isArray(res?.amenities) ? res.amenities : null,
                    description: typeof res?.description === 'string' ? res.description : null,
                });
                setTranslating(false);
            })
            .fail(() => {
                if (!cancelled) {
                    setTranslating(false);
                }
            });
        return () => {
            cancelled = true;
        };
        // Once per open: a recheck that re-resolves the same details must not re-request.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const original = !enabled || showOriginal;
    return {
        translatedExists,
        showOriginal,
        toggleOriginal: () => setShowOriginal((previous) => !previous),
        translating,
        amenities: original ? amenities : translated.amenities || amenities,
        description: original ? description : translated.description || description,
        dir: original ? LTR : i18n.dir(),
        headingOptions: original ? ENGLISH_HEADINGS : undefined,
    };
}

export default useRm3RoomTranslation;
